import { movies } from "@/mocks/movies";

export type MovieType = "drama" | "romance" | "society";

export interface Movie {
  id: string;
  title: string;
  originalTitle: string;
  year: number;
  type: MovieType;
  director: string;
  cast: string[];
  duration: number;
  rating: number;
  views: number;
  ageRating: string;
  releaseDate: string;
  featured: boolean;
  tags: string[];
  synopsis: string;
  poster: string;
  backdrop?: string;
}

export interface CategoryMeta {
  slug: string;
  name: string;
  en: string;
  tagline: string;
  description: string;
  accent: string;
}

export const allMovies: Movie[] = movies as Movie[];

export const categories: CategoryMeta[] = [
  {
    slug: "drama",
    name: "剧情伦理",
    en: "DRAMA",
    tagline: "在生活的褶皱里，看见人性",
    description:
      "聚焦家庭、婚姻与个人命运的韩国剧情伦理电影，擅长用克制的镜头语言讲述普通人的困境与选择。本分类收录《无声的潮汐》《冬柏花开时》《雨落北村》等高分作品。",
    accent: "剧情",
  },
  {
    slug: "romance",
    name: "爱情伦理",
    en: "ROMANCE",
    tagline: "克制的情感，滚烫的心",
    description:
      "以都市男女的情感关系为核心，探讨距离、错过与重逢的爱情伦理电影。热门影片包括《午夜电台的告白》《海边旅馆的房客》《秋天结束前》。",
    accent: "爱情",
  },
  {
    slug: "society",
    name: "社会伦理",
    en: "SOCIETY",
    tagline: "一个人的良心，一座城的回声",
    description:
      "将镜头对准职场、阶层与公共事件的韩国社会伦理电影，冷静而有力。代表作有《被删掉的采访》《沉默的合同》《一座城的良心》。",
    accent: "社会",
  },
  {
    slug: "all",
    name: "全部片库",
    en: "LIBRARY",
    tagline: "完整片单，一页览尽",
    description:
      "韩国伦理电影在线观看完整片库，收录剧情、爱情、社会三大分类的全部影片，支持按年份与评分筛选排序。",
    accent: "片库",
  },
];

export function getCategory(slug?: string): CategoryMeta {
  return categories.find((item) => item.slug === slug) ?? categories[0];
}

export function getMovieById(id?: string): Movie | undefined {
  return allMovies.find((movie) => movie.id === id);
}

export function getFeaturedMovies(): Movie[] {
  return allMovies.filter((movie) => movie.featured);
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...allMovies]
    .sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1))
    .slice(0, limit);
}

export function getTopRatedMovies(limit = 6): Movie[] {
  return [...allMovies].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  return allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const sharedTags = item.tags.filter((tag) => movie.tags.includes(tag)).length;
      const typeScore = item.type === movie.type ? 3 : 0;
      return { movie: item, score: sharedTags * 2 + typeScore + item.rating / 10 };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.movie);
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} 分钟`;
  return `${hours} 小时 ${rest} 分`;
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
}

export const yearOptions = Array.from({ length: 10 }, (_, index) => 2024 - index);

export const sortOptions = [
  { value: "rating", label: "评分从高到低" },
  { value: "latest", label: "最新上映优先" },
  { value: "views", label: "最多人观看" },
] as const;

export type SortValue = (typeof sortOptions)[number]["value"];

export function filterMovies(
  list: Movie[],
  options: { year: string; minRating: string; sort: SortValue }
): Movie[] {
  let result = [...list];

  if (options.year !== "all") {
    if (options.year === "2019") {
      result = result.filter((movie) => movie.year <= 2019);
    } else {
      result = result.filter((movie) => movie.year === Number(options.year));
    }
  }

  if (options.minRating !== "all") {
    result = result.filter((movie) => movie.rating >= Number(options.minRating));
  }

  result.sort((a, b) => {
    if (options.sort === "latest") return a.releaseDate < b.releaseDate ? 1 : -1;
    if (options.sort === "views") return b.views - a.views;
    return b.rating - a.rating;
  });

  return result;
}

export function searchMovies(keyword: string): Movie[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return allMovies.filter((movie) => {
    const typeName = categories.find((item) => item.slug === movie.type)?.name ?? "";
    const haystack = [
      movie.title,
      movie.originalTitle,
      movie.director,
      typeName,
      ...movie.cast,
      ...movie.tags,
      String(movie.year),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}